import { X, Paperclip, Upload, User } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const AttachmentModal = ({
  isOpen,
  onClose,
  onSave,
  attachment = null,
  users = [],
}) => {
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    userIds: [],
    file: null,
  });

  const [errors, setErrors] = useState({});
  const [filePreview, setFilePreview] = useState(null);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose();
    };

    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (attachment) {
      setFormData({
        name: attachment.name || "",
        userIds: attachment.users ? attachment.users.map((a) => a.id) : [],
        file: null,
      });

      setFilePreview(attachment.filePath || null);
    } else {
      setFormData({
        name: "",
        userIds: [],
        file: null,
      });
      setFilePreview(null);
    }
    setErrors({});
  }, [attachment, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.userIds || formData.userIds.length === 0)
      newErrors.userIds = "Selecione pelo menos um responsável";
    if (!attachment && !formData.file) newErrors.file = "Arquivo é obrigatório";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, file }));

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }

      if (errors.file) {
        setErrors((prev) => ({ ...prev, file: "" }));
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const attachmentData = new FormData();

      attachmentData.append("name", formData.name);
      formData.userIds.forEach((id) => attachmentData.append("userIds", id));

      if (formData.file) {
        attachmentData.append("file", formData.file);
      }

      if (attachment?.id) {
        attachmentData.append("id", attachment.id);
      }

      onSave(attachmentData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            {/* Cabeçalho */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <Paperclip className="mr-2 h-5 w-5" />
                {attachment ? "Editar Anexo" : "Novo Anexo"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                {/* Nome do Anexo */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Paperclip className="mr-2 h-4 w-4" />
                    Nome do Anexo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o nome do anexo"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                {/* Seleção de Responsáveis */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <User className="mr-2 h-4 w-4" />
                    Responsáveis pelo Documento *
                  </label>

                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
                    {users.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Nenhum usuário encontrado.
                      </p>
                    )}

                    {users.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center space-x-2 py-1 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <input
                          type="checkbox"
                          checked={formData.userIds?.includes(user.id) || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev) => {
                              const current = new Set(prev.userIds || []);
                              if (checked) current.add(user.id);
                              else current.delete(user.id);
                              return { ...prev, userIds: Array.from(current) };
                            });
                            if (errors.userIds)
                              setErrors((prev) => ({ ...prev, userIds: "" }));
                          }}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800"
                        />
                        <span>{user.name}</span>
                      </label>
                    ))}
                  </div>

                  {errors.userIds && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.userIds}
                    </p>
                  )}
                </div>

                {/* Upload de Arquivo */}
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Upload className="mr-2 h-4 w-4" />
                    Arquivo {!attachment && "*"}
                  </label>

                  <div
                    onClick={handleUploadClick}
                    className={`cursor-pointer rounded-lg border-2 border-dashed ${
                      errors.file
                        ? "border-red-500"
                        : "border-gray-300 hover:border-green-500"
                    } bg-gray-50 p-6 text-center transition-colors dark:border-gray-600 dark:bg-gray-800`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept="*/*"
                    />

                    {formData.file || filePreview ? (
                      <div className="space-y-3">
                        {filePreview &&
                          formData.file?.type?.startsWith("image/") && (
                            <img
                              src={filePreview}
                              alt="Preview"
                              className="mx-auto h-32 w-32 rounded-lg object-cover"
                            />
                          )}
                        <div className="flex items-center justify-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                          <Paperclip className="h-4 w-4" />
                          <span className="font-medium">
                            {formData.file?.name || "Arquivo atual"}
                          </span>
                        </div>
                        {formData.file && (
                          <p className="text-xs text-gray-500">
                            {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          Clique para alterar o arquivo
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Clique para fazer upload
                        </p>
                        <p className="text-xs text-gray-500">
                          Qualquer tipo de arquivo
                        </p>
                      </div>
                    )}
                  </div>

                  {errors.file && (
                    <p className="mt-1 text-sm text-red-500">{errors.file}</p>
                  )}
                </div>

                {/* Preview */}
                <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                  <div>
                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Pré-visualização:
                    </span>
                    <div className="mt-2 space-y-2 rounded-lg border border-gray-300 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {formData.name || "Nome do Anexo"}
                      </p>
                      <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                        <User className="h-4 w-4" />
                        <span>
                          {formData.userIds?.length > 0
                            ? users
                                .filter((u) => formData.userIds.includes(u.id))
                                .map((u) => u.name)
                                .join(", ")
                            : "Nenhum responsável selecionado"}
                        </span>
                      </div>
                      {(formData.file || filePreview) && (
                        <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
                          <Paperclip className="h-4 w-4" />
                          <span>
                            {formData.file?.name || "Arquivo anexado"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                >
                  {attachment ? "Salvar Alterações" : "Criar Anexo"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AttachmentModal;
